<template>
  <text-box :label="titleString" :icon="icon">
    <template v-slot:info-dialog><slot name="info-dialog"></slot></template>
    <!-- Column headers as HTML so they can carry real mdi icons. The row mirrors the
         chart grid margins and splits into two centered halves, so each title sits at
         25%/75% of the plot area — the centers of the two category bands. -->
    <div class="d-flex font-weight-bold" :style="{ marginLeft: '10px', marginRight: `${LEGEND_WIDTH}px`, paddingTop: '15px' }">
      <div class="text-center" style="width: 50%;"><v-icon class="mr-1" small>mdi-{{ actualIcon }}</v-icon>{{ actualLabel }}</div>
      <div class="text-center" style="width: 50%;"><v-icon class="mr-1" small>mdi-{{ optimizedIcon }}</v-icon>{{ optimizedLabel }}</div>
    </div>
    <v-chart :option="options" :init-options="{renderer: 'canvas'}" autoresize style="width: 100%; height: 440px; margin-bottom: 15px;"
             @legendselectchanged="onLegendSelectChanged" @legendselectall="onLegendSelectAll" @legendinverseselect="onLegendInverseSelect"/>
  </text-box>
</template>

<script>
import ECharts from 'vue-echarts'
import 'echarts/lib/chart/bar'
import 'echarts/lib/chart/custom'
import 'echarts/lib/component/tooltip'
import 'echarts/lib/component/grid'
import 'echarts/lib/component/legend'
import 'echarts/lib/component/toolbox'
import 'zrender/lib/canvas/canvas'
import numbro from 'numbro'

import TextBox from '@/components/TextBox.vue'
import { copyTextToClipboard } from '@/utils/clipboard.js'

// Fraction of the category band each bar occupies; the flow bands' renderItem
// derives the bar edges from this same value, so keep them in sync.
const BAR_WIDTH_FRACTION = 0.4
// Space reserved for the vertical legend; the column headers' right margin must
// match so they stay centered over the bars.
const LEGEND_WIDTH = 170
// The mdi content-copy and check glyphs, drawn inline: the Data View panel is
// built with plain DOM calls, outside the template where v-icon lives.
const COPY_ICON_PATH = 'M19,21H8V7H19M19,5H8A2,2 0 0,0 6,7V21A2,2 0 0,0 8,23H19A2,2 0 0,0 21,21V7A2,2 0 0,0 19,5M16,1H4A2,2 0 0,0 2,3V17H4V3H16V1Z'
const CHECK_ICON_PATH = 'M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,6.59L21,7Z'

export default {
  data () {
    return {
      LEGEND_WIDTH: LEGEND_WIDTH,
      // Legend selection state, keyed by channel name; missing keys mean selected.
      // Kept in sync via the legend events so the flow bands can follow the
      // re-stacked bars when channels are toggled.
      selectedSeries: {}
    }
  },
  computed: {
    // Single source of truth: union of both plans' channels with each plan's spend and
    // share of total, sorted ascending by actual share (ECharts stacks series bottom-up,
    // so ascending order puts the largest segment at the top).
    channelData () {
      const actualMix = (this.actual && this.actual.mix) || {}
      const optimizedMix = (this.optimized && this.optimized.mix) || {}
      const investOf = function (mix, name) {
        const entry = mix[name]
        return (entry && typeof entry.invest === 'number' && !isNaN(entry.invest)) ? entry.invest : 0
      }
      const totalOf = function (plan, mix) {
        if (plan && typeof plan.budget === 'number' && plan.budget > 0) return plan.budget
        return Object.keys(mix).reduce((acc, name) => acc + investOf(mix, name), 0)
      }
      const names = {}
      Object.keys(actualMix).forEach(name => { names[name] = true })
      Object.keys(optimizedMix).forEach(name => { names[name] = true })
      const actualTotal = totalOf(this.actual, actualMix)
      const optimizedTotal = totalOf(this.optimized, optimizedMix)
      const channels = Object.keys(names).map(name => ({
        name: name,
        actualSpend: investOf(actualMix, name),
        optimizedSpend: investOf(optimizedMix, name),
        actualPct: actualTotal > 0 ? 100 * investOf(actualMix, name) / actualTotal : 0,
        optimizedPct: optimizedTotal > 0 ? 100 * investOf(optimizedMix, name) / optimizedTotal : 0
      }))
      channels.sort((a, b) => (a.actualPct - b.actualPct) || a.name.localeCompare(b.name))
      return channels
    },
    // The currently selected channels with the cumulative stack offsets the flow
    // bands need. ECharts re-stacks the bars over selected series only, so the
    // offsets must accumulate over the same subset to keep the bands aligned.
    visibleChannelData () {
      const channels = this.channelData
        .filter(channel => this.selectedSeries[channel.name] !== false)
        .map(channel => ({ ...channel }))
      let actualCum = 0
      let optimizedCum = 0
      channels.forEach(channel => {
        channel.actualLow = actualCum
        actualCum += channel.actualPct
        channel.actualHigh = actualCum
        channel.optimizedLow = optimizedCum
        optimizedCum += channel.optimizedPct
        channel.optimizedHigh = optimizedCum
      })
      return channels
    },
    // Hide labels on segments too small relative to the visible stack to fit one.
    // Relative to the visible total (not a fixed 1.5%) so that when large channels
    // are toggled off and the y-axis rescales, small segments get their labels back.
    labelThreshold () {
      const visibleMax = this.visibleChannelData.reduce(
        (acc, channel) => ({ actual: acc.actual + channel.actualPct, optimized: acc.optimized + channel.optimizedPct }),
        { actual: 0, optimized: 0 }
      )
      return Math.max(visibleMax.actual, visibleMax.optimized) * 0.015
    },
    // Legend lists channels by actual share descending (top segment first).
    legendNames () {
      return this.channelData.slice().reverse().map(channel => channel.name)
    },
    // Explicit legend selection map. The options computed returns a fresh object on
    // every selection change, which makes vue-echarts setOption with notMerge — that
    // would reset ECharts' internal legend state back to all-selected, so the state
    // must be declared in the option itself.
    legendSelected () {
      return Object.fromEntries(
        this.channelData.map(channel => [channel.name, this.selectedSeries[channel.name] !== false])
      )
    },
    series () {
      const series = this.channelData.map(channel => ({
        name: channel.name,
        type: 'bar',
        stack: 'mix',
        barWidth: `${BAR_WIDTH_FRACTION * 100}%`,
        itemStyle: {
          color: this.colorOf(channel.name)
        },
        label: {
          show: true,
          position: 'inside',
          formatter: params => params.value >= this.labelThreshold ? this.formatPercent(params.value) : ''
        },
        labelLayout: {
          hideOverlap: true
        },
        data: [channel.actualPct, channel.optimizedPct]
      }))
      series.push({
        name: 'flows',
        type: 'custom',
        silent: true,
        z: 1,
        tooltip: {
          show: false
        },
        renderItem: this.renderFlow,
        data: this.visibleChannelData.map(channel => [channel.actualLow, channel.actualHigh, channel.optimizedLow, channel.optimizedHigh])
      })
      return series
    },
    options () {
      return {
        textStyle: {
          fontFamily: '"Roboto","Helvetica",sans-serif',
          lineHeight: 1.2381,
          fontWeight: 400
        },
        grid: {
          // The toolbox sits in the top-left corner of the canvas, so the plot
          // area starts below it rather than under the icons.
          top: this.export ? 30 : 10,
          left: 10,
          right: LEGEND_WIDTH,
          bottom: 10,
          containLabel: true
        },
        toolbox: {
          show: this.export,
          left: 0,
          top: 0,
          feature: {
            dataView: {
              readOnly: true,
              title: this.$t('components.simulationDetails.ComparativeMixChart.dataView'),
              lang: [this.$t('components.simulationDetails.ComparativeMixChart.dataView'), this.$t('components.simulationDetails.ComparativeMixChart.close'), this.$t('components.simulationDetails.ComparativeMixChart.refresh')],
              optionToContent: this.dataViewContent
            },
            saveAsImage: {
              type: 'png',
              name: this.exportImageName || this.titleString || 'ComparativeMix',
              title: this.$t('components.simulationDetails.ComparativeMixChart.saveAsImage')
            }
          },
          showTitle: false,
          tooltip: {
            show: true,
            formatter: function (param) {
              return param.title
            }
          }
        },
        xAxis: {
          type: 'category',
          // Headers are rendered as HTML above the chart (they carry mdi icons).
          data: [this.actualLabel, this.optimizedLabel],
          axisLine: {
            show: false
          },
          axisTick: {
            show: false
          },
          axisLabel: {
            show: false
          }
        },
        yAxis: {
          type: 'value',
          min: 0,
          // No fixed max: when channels are toggled off the axis rescales to the
          // visible stack, letting small channels be appreciated.
          show: false,
          splitLine: {
            show: false
          }
        },
        legend: {
          orient: 'vertical',
          right: 15,
          top: 'middle',
          type: 'scroll',
          selector: this.channelData.length > 1 ? [
            {
              type: 'all',
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.selectAll')
            },
            {
              type: 'inverse',
              title: this.$t('components.simulationDetails.ChannelCurvesChartMultiInstance.invert')
            }
          ] : null,
          data: this.legendNames,
          selected: this.legendSelected
        },
        tooltip: {
          trigger: 'item',
          formatter: this.tooltipFormatter
        },
        series: this.series
      }
    }
  },
  methods: {
    colorOf (name) {
      return (this.colors && this.colors[name]) || '#999999'
    },
    // Draws the semi-transparent band joining a channel's segment in the actual bar
    // with its segment in the optimized bar. Data per channel:
    // [actualLow, actualHigh, optimizedLow, optimizedHigh] cumulative percentages.
    renderFlow (params, api) {
      const actualLow = api.value(0)
      const actualHigh = api.value(1)
      const optimizedLow = api.value(2)
      const optimizedHigh = api.value(3)
      if (actualHigh - actualLow <= 0 && optimizedHigh - optimizedLow <= 0) return null
      const halfBar = api.size([1, 0])[0] * BAR_WIDTH_FRACTION / 2
      const left = api.coord([0, 0])[0] + halfBar
      const right = api.coord([1, 0])[0] - halfBar
      const channel = this.visibleChannelData[params.dataIndex]
      return {
        type: 'polygon',
        shape: {
          points: [
            [left, api.coord([0, actualHigh])[1]],
            [right, api.coord([1, optimizedHigh])[1]],
            [right, api.coord([1, optimizedLow])[1]],
            [left, api.coord([0, actualLow])[1]]
          ]
        },
        style: {
          fill: this.colorOf(channel.name),
          opacity: 0.3
        }
      }
    },
    // Whichever bar is hovered, the tooltip shows the channel's full comparison as a
    // compact aligned table: actual and optimized rows (marked with the same orange/blue
    // dots as the Incremental Sales Comparison series), and a bold difference row with
    // the deltas in amount and share points.
    tooltipFormatter (params) {
      if (!params || params.seriesName === 'flows') return ''
      const channel = this.channelData.find(c => c.name === params.seriesName)
      if (!channel) return ''
      const unit = this.spendUnitName ? ` ${this.spendUnitName}` : ''
      const dot = color => color
        ? `<span style="display:inline-block;width:10px;height:10px;border-radius:50%;background-color:${color};margin-right:5px;"></span>`
        : ''
      const row = (marker, label, spendFmt, pctFmt, rowStyle) =>
        `<tr style="${rowStyle || ''}">` +
        `<td style="padding:2px 14px 2px 0;">${marker}${label}</td>` +
        `<td style="padding:2px 10px 2px 0;text-align:right;">${spendFmt}${unit}</td>` +
        `<td style="padding:2px 0;text-align:right;">${pctFmt}</td>` +
        '</tr>'
      return `<div style="font-weight:bold;margin-bottom:4px;">${params.marker}${channel.name}</div>` +
        '<table style="border-collapse:collapse;">' +
        row(dot(this.actualColor), this.actualLabel,
          this.formatSpend(channel.actualSpend), this.formatPercent(channel.actualPct)) +
        row(dot(this.optimizedColor), this.optimizedLabel,
          this.formatSpend(channel.optimizedSpend), this.formatPercent(channel.optimizedPct)) +
        row('', this.differenceLabel,
          this.formatSpend(channel.optimizedSpend - channel.actualSpend, true),
          this.formatPercent(channel.optimizedPct - channel.actualPct, true),
          'font-weight:bold;border-top:1px solid #ccc;') +
        '</table>'
    },
    // The rows behind the Data View, header first, then one channel per row in
    // legend order (top segment first). Written by hand rather than left to
    // ECharts' default table: the bars carry shares only, so the default would
    // drop the spends entirely and print the flow bands' raw
    // [low, high, low, high] arrays. Every channel is listed, not just the
    // selected ones, with the same figures the tooltip shows. Shared by the table
    // and the clipboard copy, so what gets pasted is what is read on screen.
    dataViewRows () {
      const t = key => this.$t(`components.simulationDetails.ComparativeMixChart.${key}`)
      const unit = this.spendUnitName ? ` (${this.spendUnitName})` : ''
      const header = [
        t('channel'),
        `${this.actualLabel} · ${t('spend')}${unit}`,
        `${this.actualLabel} · ${t('share')}`,
        `${this.optimizedLabel} · ${t('spend')}${unit}`,
        `${this.optimizedLabel} · ${t('share')}`,
        `${this.differenceLabel} · ${t('spend')}${unit}`,
        `${this.differenceLabel} · ${t('share')}`
      ]
      const rows = this.channelData.slice().reverse().map(channel => [
        channel.name,
        this.formatTableSpend(channel.actualSpend),
        this.formatPercent(channel.actualPct),
        this.formatTableSpend(channel.optimizedSpend),
        this.formatPercent(channel.optimizedPct),
        this.formatTableSpend(channel.optimizedSpend - channel.actualSpend, true),
        this.formatPercent(channel.optimizedPct - channel.actualPct, true)
      ])
      return [header].concat(rows)
    },
    // The Data View panel: a Copy button over the table of dataViewRows. Built as
    // a DOM node rather than an HTML string so the button can carry a real click
    // handler, and so channel names go in as text (a pretty name containing '&'
    // or '<' would otherwise be read as markup).
    dataViewContent () {
      const root = document.createElement('div')
      // zrender calls disableUserSelect on the chart's root element so dragging
      // the canvas doesn't select the page, and the Data View panel lives inside
      // it — hence a table whose text could not even be highlighted. ECharts'
      // default view never hit this because it is a textarea. Selection is turned
      // back on for the panel's own content.
      root.style.cssText = 'padding:0 15px 15px;overflow:auto;user-select:text;-webkit-user-select:text;'
      const rows = this.dataViewRows()
      const table = document.createElement('table')
      table.style.cssText = 'border-collapse:collapse;font-size:12px;'
      rows.forEach((row, rowIndex) => {
        const tr = document.createElement('tr')
        row.forEach((value, colIndex) => {
          const cell = document.createElement(rowIndex === 0 ? 'th' : 'td')
          cell.textContent = value
          cell.style.cssText = `padding:${rowIndex === 0 ? '5px' : '4px'} 10px;` +
            `text-align:${colIndex === 0 ? 'left' : 'right'};` +
            (rowIndex === 0 ? 'border-bottom:1px solid #ccc;white-space:nowrap;' : '')
          tr.appendChild(cell)
        })
        table.appendChild(tr)
      })
      root.appendChild(table)
      // The Copy control belongs next to the panel's "Data View" title, which
      // ECharts builds outside optionToContent's reach — at this point the
      // returned node has no parent yet. The button is moved onto that header on
      // the next tick, once ECharts has assembled the panel and put it in the
      // document; if the header isn't where it is expected, it stays above the
      // table so the copy is never simply lost.
      const button = this.dataViewCopyButton(rows)
      root.appendChild(button)
      setTimeout(() => {
        const viewMain = root.parentNode
        const panel = viewMain && viewMain.parentNode
        const header = panel && panel.querySelector('h4')
        if (header) {
          button.style.margin = '0 0 0 10px'
          header.appendChild(button)
        }
      })
      return root
    },
    // Copies the table as TSV, which pastes into a spreadsheet as columns. The
    // values are the formatted ones, so the paste reads like the panel. Icon only,
    // so the outcome of a click is carried by the glyph and the tooltip.
    dataViewCopyButton (rows) {
      const t = key => this.$t(`components.simulationDetails.ComparativeMixChart.${key}`)
      const button = document.createElement('button')
      const idle = () => {
        button.innerHTML = this.svgIcon(COPY_ICON_PATH)
        button.style.color = '#2979c9'
        button.title = t('copy')
        button.setAttribute('aria-label', t('copy'))
      }
      button.type = 'button'
      button.style.cssText = 'margin:0 0 10px;padding:2px;line-height:0;vertical-align:middle;' +
        'background:none;border:none;cursor:pointer;'
      idle()
      button.onclick = () => {
        copyTextToClipboard(rows.map(row => row.join('\t')).join('\n')).then(copied => {
          button.innerHTML = this.svgIcon(copied ? CHECK_ICON_PATH : COPY_ICON_PATH)
          button.style.color = copied ? '#4caf50' : '#c62828'
          button.title = copied ? t('copied') : t('copyFailed')
          // Back to the idle icon; if the panel was closed meanwhile this just
          // writes to a detached node.
          setTimeout(idle, 2000)
        })
      }
      return button
    },
    svgIcon (path) {
      return `<svg viewBox="0 0 24 24" width="18" height="18" style="vertical-align:middle;">` +
        `<path fill="currentColor" d="${path}"/></svg>`
    },
    onLegendSelectChanged (params) {
      this.selectedSeries = params.selected
    },
    onLegendSelectAll () {
      this.selectedSeries = Object.fromEntries(
        this.channelData.map(channel => [channel.name, true])
      )
    },
    onLegendInverseSelect () {
      this.selectedSeries = Object.fromEntries(
        this.channelData.map(channel => [channel.name, this.selectedSeries[channel.name] === false])
      )
    },
    formatSpend (value, forceSign) {
      return numbro(value).format({ average: true, lowPrecision: false, mantissa: 1, optionalMantissa: true, forceSign: !!forceSign })
    },
    formatPercent (value, forceSign) {
      return numbro(value).format({ mantissa: 2, forceSign: !!forceSign }) + '%'
    },
    // Spends in the Data View are written out in full — the abbreviated form the
    // tooltip uses ("1.2M") loses the precision of a number meant to be read off
    // the table or copied out of it.
    formatTableSpend (value, forceSign) {
      return numbro(value).format({ thousandSeparated: true, mantissa: 0, forceSign: !!forceSign })
    }
  },
  props: {
    actual: {
      type: Object,
      required: true
    },
    optimized: {
      type: Object,
      required: true
    },
    colors: {
      type: Object,
      required: false,
      default: function () {
        return {}
      }
    },
    actualLabel: {
      type: String,
      required: true
    },
    optimizedLabel: {
      type: String,
      required: true
    },
    actualIcon: {
      type: String,
      required: false,
      default: function () {
        return 'chart-bar'
      }
    },
    optimizedIcon: {
      type: String,
      required: false,
      default: function () {
        return 'trending-up'
      }
    },
    // Tooltip row markers for the actual and optimized plans.
    actualColor: {
      type: String,
      required: false,
      default: function () {
        return '#F5A623'
      }
    },
    optimizedColor: {
      type: String,
      required: false,
      default: function () {
        return '#4B67B8'
      }
    },
    differenceLabel: {
      type: String,
      required: false,
      default: function () {
        return 'Difference'
      }
    },
    titleString: {
      type: String,
      required: true
    },
    spendUnitName: {
      type: String,
      required: false
    },
    icon: {
      type: String,
      required: false,
      default: function () {
        return 'chart-bar-stacked'
      }
    },
    // Shows the toolbox (Data View + Save as Image), like the other plan charts.
    export: {
      type: Boolean,
      required: false,
      default: function () {
        return false
      }
    },
    exportImageName: {
      type: String,
      required: false
    }
  },
  components: {
    'v-chart': ECharts,
    TextBox
  }
}
</script>

<style scoped>
</style>
